import React, { useState, useEffect } from "react";
import SEO from "../seo";
import { TRANSACTION_FEES } from "../constant_data";
const BillDetails = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "subscription-fees",
        "transaction-fees",
        "threshold-payments",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6 ">
      <SEO
        title="Bill Details – xPage Help Center"
        description="Understand your xPage bill, subscription fees, transaction fees, and threshold payments. Learn how charges are calculated and what you pay."
        keywords="xPage, billing, subscription fees, transaction fees, threshold payments, help center"
        canonical="https://help.xpage.ai/help/understand-xpage/Bill%20Details%20(What's%20Included)"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          What's Included in Your xPage Bill
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Your xPage bill shows all charges related to your subscription, store
          activities, and services used. This helps you understand exactly what
          you're paying for and why.
        </p>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Here's what you might see on your bill:
            </p>
          </div>
        </section>

        {/* Subscription Fees Section */}
        <section id="subscription-fees" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Subscription Fees
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-core/10 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-5 h-5 text-core"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-systemTitle">
                Subscription Fees
              </h3>
            </div>

            <ul className="space-y-2">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-core rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span className="text-sm text-systemLabel">
                  The monthly or annual cost of your chosen plan (Starter,
                  xPage, Pro, or Enterprise).
                </span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-core rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span className="text-sm text-systemLabel">
                  If you change plans during a billing cycle, your bill will
                  reflect a prorated charge or credit.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Transaction Fees Section */}
        <section id="transaction-fees" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Transaction Fees
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-core/10 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-5 h-5 text-core"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-systemTitle">
                Transaction Fees
              </h3>
            </div>

            <ul className="space-y-2">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-core rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span className="text-sm text-systemLabel">
                  A percentage charged on each sale based on your plan's card
                  rate (e.g., {TRANSACTION_FEES.STARTER.CARD}%,{" "}
                  {TRANSACTION_FEES.GROWTH.CARD}%, {TRANSACTION_FEES.PRO.CARD}%,
                  or {TRANSACTION_FEES.ENTERPRISE.CARD}%).
                </span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-core rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span className="text-sm text-systemLabel">
                  0% Cash on Delivery (COD) processing fees.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Threshold Payments Section */}
        <section id="threshold-payments" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Threshold Payments
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-core/10 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-5 h-5 text-core"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-systemTitle">
                Threshold Payments
              </h3>
            </div>

            <ul className="space-y-2">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-core rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span className="text-sm text-systemLabel">
                  Transaction fees are charged when your accumulated fees reach
                  your plan's threshold (starting at $15 and increasing as you
                  meet payment milestones).
                </span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-core rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span className="text-sm text-systemLabel">
                  Threshold adjustments may occur based on your payment history
                  or store activity.
                </span>
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#subscription-fees"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "subscription-fees"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Subscription Fees
              </a>
            </li>
            <li>
              <a
                href="#transaction-fees"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "transaction-fees"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Transaction Fees
              </a>
            </li>
            <li>
              <a
                href="#threshold-payments"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "threshold-payments"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Threshold Payments
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BillDetails;
